/**
 * Global Authentication Middleware for TalkCel
 */
import { safeErrorMessage } from '~/types/errors'

export default defineNuxtRouteMiddleware(async (to) => {
  // Guard against missing Supabase configuration (no .env set up yet)
  let user: any
  let supabase: any
  try {
    user = useSupabaseUser()
    supabase = useSupabaseClient()
  } catch (err) {
    console.warn('Supabase not configured, allowing navigation')
    return
  }

  // If Supabase client is not properly initialized, skip auth checks
  if (!supabase?.auth) {
    return
  }

  const publicRoutes = [
    '/login', '/register', '/confirm', '/invite', '/reset-password', '/forgot-password', '/auth/callback',
    '/', '/about', '/contact', '/features', '/pricing', '/privacy', '/imprint', '/terms'
  ]

  // Strip i18n locale prefix for route matching
  const localePattern = /^\/(?:de)\b/
  const hasLocalePrefix = localePattern.test(to.path)
  const normalizedPath = to.path.replace(localePattern, '') || '/'

  const isPublicRoute = publicRoutes.some(route => {
    if (route === '/') return normalizedPath === '/'
    return normalizedPath === route || normalizedPath.startsWith(route + '/')
  })
  const isApiRoute = to.path.startsWith('/api/')

  if (isPublicRoute || isApiRoute) return

  // Redirect app routes away from locale prefix
  if (hasLocalePrefix && !isPublicRoute) {
    const query = to.fullPath.includes('?') ? '?' + to.fullPath.split('?')[1].split('#')[0] : ''
    return navigateTo(normalizedPath + query + (to.hash || ''), { redirectCode: 301 })
  }

  if (!user.value) {
    return navigateTo('/login')
  }

  try {
    const { data: { session }, error } = await supabase.auth.getSession()

    if (error || !session) {
      const { data: refreshData, error: refreshError } = await supabase.auth.refreshSession()
      if (refreshError || !refreshData.session) {
        await supabase.auth.signOut()
        return navigateTo('/login')
      }
    }

    const userStore = useUserStore()
    userStore.setUser(user.value)

    if (!userStore.profile) {
      await userStore.fetchProfile(true)
    }

    const impersonationStore = useImpersonationStore()
    if (!impersonationStore.lastFetchTime || Date.now() - impersonationStore.lastFetchTime > 60_000) {
      await impersonationStore.fetchStatus()
    }

    const hasCompletedOnboarding = userStore.hasCompletedOnboarding
    const isOnboardingPage = to.path.startsWith('/onboarding')

    if (!hasCompletedOnboarding && !isOnboardingPage) {
      return navigateTo('/onboarding')
    }

    if (hasCompletedOnboarding && isOnboardingPage) {
      return navigateTo('/dashboard')
    }
  } catch (err: unknown) {
    console.warn('Session validation error:', safeErrorMessage(err))
  }
})
